import React, { useMemo } from 'react';
import ChartCanvas from '../ChartCanvas';
import PlatformChips from '../PlatformChips';
import {
  SERIES_GROUPS, PALETTE, platformColor, shade, platformLabel, fmtNum, fmtMetric, fmtShortDate,
  sortAsc, combinedSeries, sumLatest, combinedChangePct, sumCountries,
  topCountries, trendPct,
} from '../soundstatsHelpers';
import { lineConfig, horizBarPaletteConfig } from '../soundstatsCharts';

function TrendHtml({ pct }) {
  if (pct == null) return null;
  const a = Math.abs(pct);
  if (a < 0.05) return <span className="ss-delta flat">— 0%</span>;
  if (pct > 0) return <span className="ss-delta up">▲ {a.toFixed(1)}%</span>;
  return <span className="ss-delta down">▼ {a.toFixed(1)}%</span>;
}

function Kpi({ value, label, children }) {
  return (
    <div className="ss-kpi">
      <div className="ss-kpi-v">{value}</div>
      <div className="ss-kpi-l">{label}</div>
      {children}
    </div>
  );
}

// Series groups: audience, social, listen, stream, popularity, retention,
// playlistReach. Platform-comparison bar, "All platforms" combined default,
// per-platform time-series + top-countries on chip select.

export default function SeriesGroup({ group, platforms, entries, chipIdx, setChipIdx }) {
  const cfg = SERIES_GROUPS[group];
  const metric = cfg.compare || (cfg.metrics[0] && cfg.metrics[0].key) || 'value';
  const metricLabel = cfg.metrics.find(m => m.key === metric)?.label || 'Value';

  const compareCfg = useMemo(() => {
    const data = [];
    platforms.forEach(e => {
      const items = sortAsc(e.items);
      const latest = items[items.length - 1];
      const v = latest ? latest[metric] : null;
      if (v != null) data.push({ label: platformLabel(e.key), value: v, color: platformColor(e.key) });
    });
    data.sort((a, b) => b.value - a.value);
    if (!data.length) return null;
    return horizBarPaletteConfig(
      data.map(d => d.label),
      data.map(d => d.value),
      data.map(d => d.color),
    );
  }, [platforms, metric]);

  const combined = useMemo(() => {
    if (chipIdx !== 0) return null;
    const allMetrics = cfg.metrics.slice(0, 4);
    return combinedSeries(platforms, allMetrics);
  }, [chipIdx, platforms, cfg.metrics]);

  const countries = useMemo(() => {
    if (chipIdx !== 0) return null;
    return sumCountries(platforms);
  }, [chipIdx, platforms]);

  const single = useMemo(() => {
    if (chipIdx === 0) return null;
    const entry = entries[chipIdx];
    if (!entry) return null;
    const items = sortAsc(entry.items);
    const metrics = cfg.metrics.filter(m => items.some(it => it[m.key] != null));
    if (!metrics.length) return { entry, items, metrics: cfg.metrics.slice(0, 1), countries: null };
    const c = topCountries(items);
    return { entry, items, metrics, countries: c };
  }, [chipIdx, entries, cfg.metrics]);

  const combinedLineCfg = useMemo(() => {
    if (!combined) return null;
    return lineConfig(combined.dates.map(fmtShortDate), combined.datasets);
  }, [combined]);

  const combinedCountriesCfg = useMemo(() => {
    if (!countries || !countries.length) return null;
    return horizBarPaletteConfig(countries.map(c => c.name), countries.map(c => c.value), PALETTE);
  }, [countries]);

  const singleLineCfg = useMemo(() => {
    if (!single) return null;
    const labels = single.items.map(it => fmtShortDate(it.date || it.positionDate));
    const base = platformColor(single.entry.key);
    const datasets = single.metrics.map((m, i) => ({
      label: m.label,
      data: single.items.map(it => it[m.key]),
      color: i === 0 ? base : shade(base, 0.22 * i),
    }));
    return lineConfig(labels, datasets);
  }, [single]);

  const singleCountriesCfg = useMemo(() => {
    if (!single || !single.countries) return null;
    return horizBarPaletteConfig(single.countries.map(c => c.name), single.countries.map(c => c.value), PALETTE);
  }, [single]);

  return (
    <>
      <div className="ss-content-title"><h2>{cfg.title}</h2><span className="ss-sec-hint">All platforms · select one to focus</span></div>

      {compareCfg && (
        <div className="ss-card" style={{ marginBottom: 14 }}>
          <div className="ss-card-head">Platforms · {metricLabel} (latest)</div>
          <ChartCanvas config={compareCfg} height={Math.max(220, platforms.length * 34)} />
        </div>
      )}

      <PlatformChips entries={entries} idx={chipIdx} onPick={setChipIdx} />

      {chipIdx === 0 ? (
        <CombinedBody cfg={cfg} platforms={platforms} metric={metric} lineCfg={combinedLineCfg} countriesCfg={combinedCountriesCfg} />
      ) : single ? (
        <SingleBody cfg={cfg} single={single} lineCfg={singleLineCfg} countriesCfg={singleCountriesCfg} />
      ) : null}
    </>
  );
}

function CombinedBody({ cfg, platforms, metric, lineCfg, countriesCfg }) {
  const { total } = sumLatest(platforms, metric);
  const pct = combinedChangePct(platforms, metric);
  const primaryLabel = cfg.metrics[0].label;
  let topPlat = null;
  platforms.forEach(e => {
    const items = sortAsc(e.items);
    const v = items.length ? items[items.length - 1][metric] : null;
    if (v != null && (!topPlat || v > topPlat.value)) topPlat = { name: platformLabel(e.key), value: v };
  });

  return (
    <div>
      <div className="ss-gkpi-grid">
        <Kpi value={fmtMetric(metric, total)} label={`Total ${primaryLabel}`}><TrendHtml pct={pct} /></Kpi>
        <Kpi value={fmtNum(platforms.length)} label="Platforms tracked" />
        {topPlat && <Kpi value={topPlat.name} label="Top platform" />}
        {cfg.metrics.filter(m => m.key !== metric).map(m => {
          const agg = sumLatest(platforms, m.key);
          return <Kpi key={m.key} value={fmtMetric(m.key, agg.total)} label={`${m.label} (total)`} />;
        })}
      </div>
      <div className="ss-card">
        <div className="ss-card-head">All platforms combined · {cfg.title} over time</div>
        {lineCfg && <ChartCanvas config={lineCfg} />}
      </div>
      {countriesCfg && (
        <div className="ss-card">
          <div className="ss-card-head">Top countries · all platforms</div>
          <ChartCanvas config={countriesCfg} height={280} />
        </div>
      )}
    </div>
  );
}

function SingleBody({ cfg, single, lineCfg, countriesCfg }) {
  const { entry, items, metrics } = single;
  const latest = items[items.length - 1] || {};
  const primary = metrics[0];
  const pct = trendPct(items, primary.key);
  const change = pct == null ? '' : `${pct > 0 ? 'up' : 'down'} ${Math.abs(pct).toFixed(1)}%`;

  return (
    <div>
      <div className="ss-gkpi-grid">
        {metrics.map(m => (
          <Kpi key={m.key} value={fmtMetric(m.key, latest[m.key])} label={m.label}><TrendHtml pct={trendPct(items, m.key)} /></Kpi>
        ))}
      </div>
      <div className="ss-card">
        <div className="ss-card-head">{platformLabel(entry.key)} · {cfg.title} over time</div>
        <div className="ss-insight">{platformLabel(entry.key)} · <b>{fmtMetric(primary.key, latest[primary.key])}</b> {primary.label.toLowerCase()}{change && <> · <span className={pct > 0 ? 'up' : 'down'}>{change} vs previous period</span></>}</div>
        {lineCfg && <ChartCanvas config={lineCfg} />}
      </div>
      {countriesCfg && (
        <div className="ss-card">
          <div className="ss-card-head">Top countries · {platformLabel(entry.key)}</div>
          <ChartCanvas config={countriesCfg} />
        </div>
      )}
    </div>
  );
}
